import { useState, type MouseEvent, type KeyboardEvent } from 'react';
import { AlertTriangle, ExternalLink, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useShareRowSummary } from '@/hooks/use-share-row-summary';

/** Sibling of the title button; never nest interactive row controls. */
export function PreviewShareLink({ pulseId, shareCategory, fillRowHeight = false }: { pulseId: number; shareCategory?: string; fillRowHeight?: boolean }) {
  const { summary: share, error, thumbnailUrl: cachedThumbnailUrl, ref } = useShareRowSummary(pulseId);
  const [failedThumbnail, setFailedThumbnail] = useState<string | null>(null);
  const hasUrl = share?.hasUrl === true;
  const alive = hasUrl && !share?.revokedAt &&
    (!share?.expiresAt || new Date(share.expiresAt).getTime() > Date.now());
  const isFinal = (share?.shareType ?? (shareCategory === 'final' ? 'final' : 'preview')) === 'final';
  const typeLabel = isFinal ? 'Final' : 'Preview';
  const aliveColor = isFinal ? '#34d391' : '#5f8ac9';
  const aliveHoverBg = isFinal ? '#34d39133' : '#6f88bf33';

  const open = (e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('open-preview-share', {
      detail: { pulseId, shareType: isFinal ? 'final' : 'preview' },
    }));
  };
  const count = share?.externalVisitCount;
  const countedSince = share?.visitsCountedSince;
  const countedDate = countedSince ? new Date(countedSince) : null;
  const visitsTitle = `Client page opens: ${count}. ${
    countedDate && Number.isFinite(countedDate.getTime())
      ? `Counted since ${countedDate.toLocaleString()}.`
      : 'Counting start unavailable.'
  } Successful page opens, not unique visitors.`;
  const thumbnailUrl = cachedThumbnailUrl && failedThumbnail !== cachedThumbnailUrl ? cachedThumbnailUrl : null;

  // Keep a tiny observation anchor even without a summary: an empty control
  // otherwise never intersects and can never register its visible row.
  return (
    <span ref={ref} className={cn("inline-flex items-center shrink-0 gap-1 ml-1.5 min-w-px min-h-px", fillRowHeight && "self-stretch")}>
      {error ? (
        <span title={error} aria-label={error} data-testid={`share-summary-error-${pulseId}`}>
          <AlertTriangle className="w-3 h-3 ml-1 text-amber-500" />
        </span>
      ) : share && !share.isDraft ? (
        <>
          {thumbnailUrl && (
            <span className={cn(
              "relative shrink-0",
              fillRowHeight ? "w-10 self-stretch min-h-5" : "w-7 h-5",
            )}>
            <img
              src={thumbnailUrl}
              alt=""
              aria-hidden="true"
              loading="lazy"
              draggable={false}
              className="absolute inset-0 w-full h-full rounded-sm object-cover"
              onError={() => setFailedThumbnail(thumbnailUrl)}
              data-testid={`share-thumbnail-${pulseId}`}
            />
            </span>
          )}
          {typeof count === 'number' && Number.isSafeInteger(count) && count >= 0 && (
            <span
              className="inline-flex items-center gap-0.5 text-[10px] tabular-nums text-gray-400"
              title={visitsTitle}
              aria-label={visitsTitle}
              data-testid={`share-visit-count-${pulseId}`}
            >
              <Eye className="w-3 h-3" aria-hidden="true" />
              {count}
            </span>
          )}
          <span
            role="button"
            tabIndex={0}
            onClick={open}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') open(e); }}
            aria-label={`Manage ${typeLabel} share${alive ? '' : ' — client link expired or revoked'}`}
            title={`Manage ${typeLabel} share${alive ? '' : ' — client link expired or revoked'}`}
            className={cn(
              'inline-flex items-center justify-center flex-shrink-0 p-0.5 rounded transition-colors cursor-pointer',
              !alive && 'text-gray-500 hover:text-gray-300 hover:bg-gray-700/40',
            )}
            style={alive ? { color: aliveColor } : undefined}
            onMouseEnter={e => { if (alive) e.currentTarget.style.backgroundColor = aliveHoverBg; }}
            onMouseLeave={e => { if (alive) e.currentTarget.style.backgroundColor = ''; }}
            data-testid={`preview-share-link-${pulseId}`}
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </span>
        </>
      ) : null}
    </span>
  );
}